import { AnimatePresence, motion } from 'framer-motion';
import { useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import Video1Poster from '../../assets/1_Authentic_Innovations.jpg';
import Video1 from '../../assets/1_Authentic_Innovations.mp4';
import Video2Poster from '../../assets/2_User_Centric.jpg';
import Video2 from '../../assets/2_User_Centric.mp4';
import Video3Poster from '../../assets/3_Precision_Performance.jpg';
import Video3 from '../../assets/3_Precision_Performance.mp4';
import Video4Poster from '../../assets/4_Global_Collaboration.jpg';
import Video4 from '../../assets/4_Global_Collaboration.mp4';
import Video5Poster from '../../assets/5_Safety_Integrity.jpg';
import Video5 from '../../assets/5_Safety_Integrity.mp4';
import { CenterLayout, HorizontalLayout, useResponsivePadding, VerticalLayout } from '../../common/common-layout';
import { H1, H4, Text1, TitleNormalBold } from '../../common/common-text';
import BTBox from '../../common/components/BTBox';
import { pxToRem } from '../../common/utils/ConvertUtils';
import '../../css/bbpage.css';
import { useDeviceType } from '../../hooks/useDeviceType';

const PAGE_COUNT = 5;

const pageContents = [
  {
    video: Video1,
    poster: Video1Poster,
    title: 'Authentic\nInnovations',
    text: '진정성 있는 혁신을 통해 산업 표준이 됩니다. 클린룸/드라이룸 설계, 반도체·2차전지·불소수지코팅기술에 이르기까지, 우리는 지속적인 혁신을 추구합니다.',
  },
  {
    video: Video2,
    poster: Video2Poster,
    title: 'User\nCentric',
    text: '사용자 중심의 설계로 최적의 배관 솔루션을 제공합니다. 고객의 요구와 기대를 충족시키기 위해 맞춤형 솔루션을 개발합니다.',
  },
  {
    video: Video3,
    poster: Video3Poster,
    title: 'Precision\nPerformance',
    text: '정밀한 성능과 뛰어난 품질로 신뢰성을 보장합니다. 우리는 제품과 서비스의 품질을 지속적으로 개선하여, 최고의 성능을 제공합니다.',
  },
  {
    video: Video4,
    poster: Video4Poster,
    title: 'Global\nCollaboration',
    text: '글로벌 협력을 통한 지식 공유와 기술 발전으로 업계를 선도합니다. 세계 각국의 파트너와 협력하여 혁신적인 솔루션을 개발하고, 기술 발전에 기여합니다.',
  },
  {
    video: Video5,
    poster: Video5Poster,
    title: 'Safety\nIntegrity',
    text: '모든 이해관계자의 안전과 건강을 최우선으로 하는 안전한 배관 솔루션을 제공합니다. 우리는 고객과 임직원 모두에게 안전한 작업 환경을 보장합니다.',
  },
];

type VisionSection3Props = {
  pageIndex: number;
  showIndicator?: boolean;
};

const VisionSection3 = ({ pageIndex, showIndicator = true }: VisionSection3Props) => {
  const { t } = useTranslation();
  const [currentIndex, setCurrentIndex] = useState(pageIndex);
  const [prevIndex, setPrevIndex] = useState<number | null>(null);
  const [zoom, setZoom] = useState(1.0);
  const { horizontal: padding } = useResponsivePadding();
  const { isMobile } = useDeviceType();

  useEffect(() => {
    const screenWidth = window.innerWidth;
    const screenHeight = window.innerHeight;
    const areaWidth = (screenWidth - padding * 2);
    if (screenHeight < areaWidth) {
      setZoom(1);
    } else {
      setZoom(areaWidth / screenHeight * 0.7);
    }
  }, [padding]);

  useEffect(() => {
    const handleResize = () => {
      const screenWidth = window.innerWidth;
      const screenHeight = window.innerHeight;
      const areaWidth = (screenWidth - padding * 2);
      if (screenHeight < areaWidth) {
        setZoom(1);
      } else {
        setZoom(areaWidth / screenHeight * 0.7);
      }
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [padding]);

  useEffect(() => {
    if (pageIndex !== currentIndex) {
      setPrevIndex(currentIndex);
      setCurrentIndex(pageIndex);
    }
  }, [pageIndex]);

  const renderPage = (index: number, isCurrent: boolean) => {
    const page = pageContents[index];

    return (
      <motion.div
        key={index}
        initial={{ opacity: isCurrent ? 0 : 1 }}
        animate={{ opacity: isCurrent ? 1 : 0 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.8, ease: 'easeInOut' }}
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          padding: '0 5%',
        }}
      >
        <HorizontalLayout wrapContents={true} style={{ alignContent: 'center', alignItems: 'center' }}>
          <video
            src={page.video}
            autoPlay
            muted
            loop
            playsInline
            poster={page.poster}
            style={{ height: `47vh`, objectFit: 'cover', zIndex: 0 }}
          />
          <BTBox width={`1vh`} />
          <VerticalLayout wrapContents={true} style={{ alignItems: 'center', width: `90vh`, paddingRight: '12vh' }}>
            <CenterLayout height={`10vh`} style={{ alignItems: 'end', alignContent: 'end' }}>
              <TitleNormalBold color='white' fontSize={`2vh`}>{index + 1}</TitleNormalBold>
            </CenterLayout>
            {isCurrent && (
              <div style={{ height: `20vh`, overflow: 'hidden', position: 'relative', alignItems: 'end', alignContent: 'end' }}>
                <motion.div
                  key={index + '-title'}
                  initial={{ y: prevIndex == null ? 200 : prevIndex < currentIndex ? 200 : -200 }}
                  animate={{ y: 0 }}
                  transition={{ duration: 0.6, ease: 'easeOut' }}
                >
                  <H1 style={{ color: 'white', textAlign: 'center', whiteSpace: 'pre-line', fontSize: `5vh`, lineHeight: `6vh` }}>
                    {t(page.title)}
                  </H1>
                </motion.div>
              </div>
            )}
            {!isCurrent && (
              <div style={{ height: `20vh` }}></div>
            )}
            <BTBox height={`${pxToRem(80)}`} />
            <Text1 style={{ color: 'white', textAlign: 'center', whiteSpace: 'pre-line', height: `18vh`, fontSize: `2.2vh`, lineHeight: '3.3vh' }}>
              <Trans i18nKey={page.text} />
            </Text1>
            <BTBox height={`${pxToRem(100)}`} />
          </VerticalLayout>
        </HorizontalLayout>
      </motion.div>
    );
  };

  return (
    <div style={{ background: 'black', height: '100vh', position: 'relative', overflow: 'hidden' }}>
      {/* 줌 적용 영역 */}
      <div style={{ transform: `scale(${isMobile ? 1.0 : zoom})`, transformOrigin: 'center', height: '100vh' }}>
        <VerticalLayout
          style={{
            background: 'black',
            height: '100vh',
            overflow: 'hidden',
            position: 'relative',
            display: 'flex',
            justifyContent: 'center', // 가로 중앙 정렬
            alignItems: 'center',     // 세로 중앙 정렬
          }}
        >
          <AnimatePresence initial={false}>
            {prevIndex !== null && renderPage(prevIndex, false)}
            {renderPage(currentIndex, true)}
          </AnimatePresence>

          {/* 비전 & 미션 */}
          <div
            style={{
              position: 'absolute',
              top: '11vh',
              left: '50%',
              transform: 'translateX(-50%)',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              width: '80%',
            }}
          >
            <H4 style={{ color: 'white', fontSize: '5vh', lineHeight: '6vh' }}>{t('비전 & 미션')}</H4>
          </div>
        </VerticalLayout>
      </div>

      {/* 인디케이터 */}
      {showIndicator ? (
        <div
          style={{
            position: 'absolute',
            bottom: `8vh`,
            left: '50%',
            transform: 'translateX(-50%)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: '32px',
            height: `${pxToRem(6)}`,
            width: `calc(100% - ${padding * 2}px)`,
            paddingLeft: padding,
            paddingRight: padding,
          }}
        >
          {Array.from({ length: PAGE_COUNT }).map((_, i) => (
            <div
              key={i}
              style={{
                flex: 1,
                height: '100%',
                backgroundColor: i === pageIndex ? '#14B446' : '#D9D9D980',
                transition: 'background-color 0.3s ease',
                borderRadius: '3px',
              }}
            />
          ))}
        </div>
      ) : (
        <div
          style={{
            position: 'absolute',
            bottom: `8vh`,
            left: '50%',
            transform: 'translateX(-50%)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: '32px',
            height: `${pxToRem(6)}`,
            width: `calc(100% - ${padding * 2}px)`,
            paddingLeft: padding,
            paddingRight: padding,
          }}
        >
        </div>
      )}
    </div>
  );

};

export default VisionSection3;
